import React, { useState, useEffect, CSSProperties } from "react";
import { SelectValidationRules } from "types";

interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  rules?: SelectValidationRules;
  className?: string;
  style?: CSSProperties;
}

const Select: React.FC<SelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  rules = {},
  className = "",
  style = {},
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = (val: string) => {
    if (rules.required) {
      const msg =
        typeof rules.required === "string"
          ? rules.required
          : `${label} is required`;
      if (!val) {
        return msg;
      }
    }

    if (rules.validate) {
      const result = rules.validate(val);
      if (result !== true) {
        return typeof result === "string" ? result : `${label} is invalid`;
      }
    }

    return null;
  };

  useEffect(() => {
    setError(validate(value));
  }, [value]);

  return (
    <div>
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-[#747171]"
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`text-sm rounded-lg block w-full p-2.5 text-gray-900
          focus:outline-none
          ${error ? "border-red-500" : "border-none"}
          `}
        style={{ backgroundColor: "#F2F2F2", border: "none", ...style }}
        required={!!rules.required}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-500"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default Select;

